import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../utils/api';
import { BaseResponse } from '../types/api';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

export interface UserRole {
  role: string;
  permissions: string[];
}

export const useRole = () => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useSelector((state: RootState) => state.auth);

  const fetchUserRole = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if user is authenticated using Redux store token
      if (!auth.token) {
        setUserRole(null);
        return;
      }
      
      // Fetch user role from API - now apiService.get directly returns the data part
      const roleData = await apiService.get<UserRole>('/api/v1/user/role');
      
      if (roleData) {
        setUserRole(roleData);
      } else {
        setUserRole(null);
        setError('Failed to fetch user role');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole(null);
      setError('Error fetching user role');
    } finally {
      setIsLoading(false);
    }
  }, [auth.token]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission: string): boolean => {
    if (!userRole || !userRole.permissions) {
      return false;
    }
    return userRole.permissions.includes(permission);
  }, [userRole]);

  // Check if user is admin
  const isAdmin = useCallback((): boolean => {
    return userRole?.role === 'admin';
  }, [userRole]);

  // Check if user is regular user
  const isUser = useCallback((): boolean => {
    return userRole?.role === 'user';
  }, [userRole]);

  // Check if user is installer
  const isInstaller = useCallback((): boolean => {
    return userRole?.role === 'installer';
  }, [userRole]);

  return {
    userRole,
    isLoading,
    error,
    fetchUserRole,
    hasPermission,
    isAdmin,
    isUser,
    isInstaller
  };
}; 